import { Type } from 'class-transformer';

import { IViewModel } from '../../interfaces/view-model.interface';
import { FormField } from './form-field.model';
import { GridRowViewModel } from './grid-row.model';

// Define the valid values for
// FormInstanceElement property 'valueType'.
export enum FormInstanceElementValueTypeEnum {
    TypeText = 'text',
    TypeInteger = 'integer',
    TypeDouble = 'double',
    TypeDecimal = 'decimal',
    TypeBoolean = 'boolean',

    TypeMultiBoolean = 'multiBoolean',
    TypeMultiText = 'multiText',
    TypeGridRows = 'gridRows'
}

export class FormInstanceElement implements IViewModel {
    public id: number = 0;

    public modifiedBy: string;
    public modifiedByUserName: string;
    public modifiedDate: Date;

    public formInstanceId: number = -1;
    public formFieldId: number = -1;

    public parentFormInstanceElementId: number = -1;
    public formField: FormField;
    public formFieldName: string;

    //VNEXT-951: KLW - Changes to bypass leave site pop-up warning
    private _bypassSitePopUpWarning: boolean = false;

    public valueType: string;
    public get ValueType(): string {
        return this.valueType;
    }
    public set ValueType(val: string) {
        this.valueType = val;
    }

    public intValue: number = 0;
    public get IntValue(): number {
        return this.intValue;
    }
    public set IntValue(val: number) {
        this.intValue = val;
    }
    public doubleValue: number = 0.0;
    public get DoubleValue(): number {
        return this.doubleValue;
    }
    public set DoubleValue(val: number) {
        this.doubleValue = val;
    }

    public decimalValue: number | null = null; // For VNEXT-2363 changed this from 0.0 to null
    public get DecimalValue(): number {
        return this.decimalValue;
    }
    public set DecimalValue(val: number) {
        this.decimalValue = val;
    }
    // Define a transient/client-only field used by the decimal form field.
    public transientOriginalDecimalValue: number;

    public customValue: string = null;
    public get CustomValue(): string {
        return this.customValue;
    }
    public set CustomValue(val: string) {
        this.customValue = val;
    }
    public textValue: string = null;
    public get TextValue(): string {
        return this.textValue;
    }
    public set TextValue(val: string) {
        this.textValue = val;
    }

    public booleanValue: boolean = false;
    public get BooleanValue(): boolean {
        return this.booleanValue;
    }
    public set BooleanValue(val: boolean) {
        this.booleanValue = val;
    }

    // Used to store the original value of a FormInstanceElement to simplify logging history
    public originalIntValue: number;
    public originalDoubleValue: number;
    public originalDecimalValue: number;
    public originalCustomValue: string;
    public originalTextValue: string;
    public originalBooleanValue: boolean;
    public originalChildFormInstanceElements: FormInstanceElement[];

    // PJH - 7/22/2025 - VNEXT-1867 - Used to flag regular/contacts/attachments grids as updated
    public gridRowsValueHasChanged: boolean; 

    public isDeleted: boolean = false; // Added 12-30-2020.

    public isGridData: boolean = false; // Added for VNEXT-917 - to indicate that a cell's data in a Home Page comes from a Grid

    // When a draft of a Form is saved and the grid is invalid, the error message gets saved to this field
    public gridValidationMessage: string;

    public transientGridCascadingDropDownName: string;

    // 12-31-2020:  changed the name of the following property from childElements
    //              to childGridRows to avoid confusion now that this class also
    //              contains new member childFormInstanceElements.
    public totalChildGridRows: number;
    @Type(() => GridRowViewModel)
    public childGridRows: GridRowViewModel[];

    @Type(() => FormInstanceElement)
    public childFormInstanceElements: FormInstanceElement[];

    // 02-22-2021 note:  added the following properties for footnotes.
    public totalFootnoteRows: number;
    @Type(() => GridRowViewModel)
    public childFootnoteRows: GridRowViewModel[];
    // End 02-22-2021 new footnote properties.

    // Define transient data (not saved in the database).
    //public transientInitializationCompleted: boolean = true;
    public transientValuesHash: any = {};
    //public transientFormField: any = null; // If assigned, this will be an instance of class FormField.
    //public transientFormInstanceElementWrapper: any = null; // If assigned, this will be an instance of class GridFormInstanceElementWrapper.
    //public transientColumnId: number; // This field is used by the grid form field.
    public transientInSetupFlag: boolean = false;
    //public transientValueSetFlag: boolean = false;
    private transientUserSetValueFlag: boolean = false;

    // Define methods related to users changing data.
    public set UserUpdatedData(bUpdatedParam: boolean) {
        this.transientUserSetValueFlag = bUpdatedParam;
    }

    public get UserUpdatedData(): boolean {
        return (this.transientUserSetValueFlag);
    }

    //VNEXT-951: KLW - Changes to bypass leave site pop-up warning
    public set BypassSitePopUpWarning(value: boolean) {
        this._bypassSitePopUpWarning = value;
    }

    public get BypassSitePopUpWarning(): boolean {
        return this._bypassSitePopUpWarning;
    }

    // Define helper methods.
    public toNumber(formField: FormField, propName: string): number {
        let property: number = <number>this[propName];

        return (property != null ? property : 0);
    }

    // Load from de-serialized network data.
    public static loadFromDeserializedNetworkData(data: FormInstanceElement): FormInstanceElement {
        let formInstanceElement: FormInstanceElement = new FormInstanceElement();

        formInstanceElement.loadFromDeserializedNetworkData(data);

        return (formInstanceElement);
    }

    public copyFieldValuesFrom(sourceFormInstanceElement: FormInstanceElement): void {
        this.textValue = sourceFormInstanceElement.textValue;
        this.intValue = sourceFormInstanceElement.intValue;
        this.decimalValue = sourceFormInstanceElement.decimalValue;
        this.booleanValue = sourceFormInstanceElement.booleanValue;
    }

    public clone(): FormInstanceElement {
        let clone: FormInstanceElement = new FormInstanceElement();

        clone.formFieldId = this.formFieldId;

        clone.valueType = this.valueType;

        clone.textValue = this.textValue;
        clone.intValue = this.intValue;
        clone.decimalValue = this.decimalValue;
        clone.booleanValue = this.booleanValue;

        return clone;
    }
    public copy(sourceFormInstanceElement: FormInstanceElement): void {
        this.valueType = sourceFormInstanceElement.valueType;

        this.textValue = sourceFormInstanceElement.textValue;
        this.intValue = sourceFormInstanceElement.intValue;
        this.decimalValue = sourceFormInstanceElement.decimalValue;
        this.booleanValue = sourceFormInstanceElement.booleanValue;
    }
    public copyWithAnyChildElements(sourceFormInstanceElement: FormInstanceElement): void {
        this.copy(sourceFormInstanceElement);

        if (sourceFormInstanceElement.childFormInstanceElements != null) {
            this.childFormInstanceElements = [];

            for (let iChild: number = 0; iChild < sourceFormInstanceElement.childFormInstanceElements.length; iChild++) {
                let childFIE: FormInstanceElement = sourceFormInstanceElement.childFormInstanceElements[iChild];
                let childClone = childFIE.clone();
                this.childFormInstanceElements.push(childClone);
            }
        }
    }

    // TODO: replace this approach with use of IFieldDefinitionLogic
    public getValueAsStringArray(): string[] {
        let value = null;
        if (this.valueType == FormInstanceElementValueTypeEnum.TypeMultiText) {
            value = this.childFormInstanceElements?.map(x => { return x.textValue }).filter(x => { return x != null });
        } else if (this.valueType == FormInstanceElementValueTypeEnum.TypeText) {
            if (this.textValue)
                value = [this.textValue];
        } else if (this.valueType == FormInstanceElementValueTypeEnum.TypeBoolean) {
            value = [`${this.booleanValue}`];
        } else if (this.valueType == FormInstanceElementValueTypeEnum.TypeDecimal) {
            if (this.decimalValue)
                value = [`${this.decimalValue}`];
        } else if (this.valueType == FormInstanceElementValueTypeEnum.TypeDouble) {
            if (this.doubleValue)
                value = [`${this.doubleValue}`];
        } else if (this.valueType == FormInstanceElementValueTypeEnum.TypeInteger) {
            value = [`${this.intValue}`];
        } else if (this.valueType == FormInstanceElementValueTypeEnum.TypeMultiBoolean) {
            value = this.childFormInstanceElements?.map(x => { return `${x.booleanValue}` });
        }

        return value;
    }

    public resetOriginalValues() {
        this.originalIntValue = this.intValue;
        this.originalDoubleValue = this.doubleValue;
        this.originalDecimalValue = this.decimalValue;
        this.originalCustomValue = this.customValue;
        this.originalTextValue = this.textValue;
        this.originalBooleanValue = this.booleanValue;
        this.originalChildFormInstanceElements = this.childFormInstanceElements;
        this.gridRowsValueHasChanged = false;
        this.UserUpdatedData = false;
    }


    // Helper methods.
    private loadFromDeserializedNetworkData(data: FormInstanceElement): void {
        this.id = data.id;

        this.formInstanceId = data.formInstanceId;
        this.formFieldId = data.formFieldId;

        this.valueType = data.valueType;

        this.intValue = data.intValue;
        this.doubleValue = data.doubleValue;
        this.decimalValue = data.decimalValue;
        this.customValue = data.customValue;
        this.textValue = data.textValue;
        this.booleanValue = data.booleanValue;

        // PJH For VNEXT-1769 - speeding up form save with new approach to history
        this.originalIntValue = data.intValue;
        this.originalDoubleValue = data.doubleValue;
        this.originalDecimalValue = data.decimalValue;
        this.originalCustomValue = data.customValue;
        this.originalTextValue = data.textValue;
        this.originalBooleanValue = data.booleanValue;

        this.totalChildGridRows = data.totalChildGridRows;
        this.childGridRows = data.childGridRows;

        //TEAMS-424: KLW - Assign the ChildFootnoteRows and TotalFootnoteRows
        this.childFootnoteRows = data.childFootnoteRows;
        this.totalFootnoteRows = data.totalFootnoteRows;

        this.gridValidationMessage = data.gridValidationMessage;

        this.childFormInstanceElements = [];
        if (data.childFormInstanceElements) {
            for (let el of data.childFormInstanceElements) {
                this.childFormInstanceElements.push(FormInstanceElement.loadFromDeserializedNetworkData(el))
            }
            // PJH For VNEXT-1809 - speeding up form save with new approach to history (use Object.assign() to ensure we have a copy and not a reference)
            this.originalChildFormInstanceElements = data.childFormInstanceElements.map(x => { return Object.assign(new FormInstanceElement(), x) });
        }

        return;
    }
}
